import Cookies from 'js-cookie';

class TokenService {
  constructor() {
    this.ACCESS_TOKEN_KEY = 'geoai_access_token';
    this.REFRESH_TOKEN_KEY = 'geoai_refresh_token';
    this.USER_KEY = 'geoai_user';
  }

  // Get access token from localStorage first, then cookies
  getAccessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY) || 
           Cookies.get(this.ACCESS_TOKEN_KEY);
  }

  // Get refresh token from localStorage first, then cookies
  getRefreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY) || 
           Cookies.get(this.REFRESH_TOKEN_KEY);
  }

  // Store tokens in both localStorage and cookies for flexibility
  setTokens(accessToken, refreshToken) {
    if (accessToken) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
      // Set cookie with 15 minutes expiry (same as backend JWT)
      Cookies.set(this.ACCESS_TOKEN_KEY, accessToken, { 
        expires: 1/96, // 15 minutes in days (1/96 = 15/1440)
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax'
      });
    }

    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
      // Set cookie with 7 days expiry (same as backend refresh token)
      Cookies.set(this.REFRESH_TOKEN_KEY, refreshToken, {
        expires: 7,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax'
      });
    }
  }

  // Remove all tokens and user data
  removeTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    
    Cookies.remove(this.ACCESS_TOKEN_KEY);
    Cookies.remove(this.REFRESH_TOKEN_KEY);
    Cookies.remove(this.USER_KEY);
  }

  // Store user data
  setUser(user) {
    if (user) {
      const userData = JSON.stringify(user);
      localStorage.setItem(this.USER_KEY, userData);
      Cookies.set(this.USER_KEY, userData, {
        expires: 7,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax'
      });
    }
  }

  // Get user data
  getUser() {
    try {
      const userData = localStorage.getItem(this.USER_KEY) || 
                      Cookies.get(this.USER_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getAccessToken();
  }

  // Check if access token is expired (basic check)
  isTokenExpired() {
    const token = this.getAccessToken();
    if (!token) return true;

    try {
      // Decode JWT payload (basic decode without verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired (with 30 second buffer)
      return payload.exp < (currentTime + 30);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  // Get token payload without verification (for client-side info only)
  getTokenPayload() {
    const token = this.getAccessToken();
    if (!token) return null;

    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (error) {
      console.error('Error decoding token payload:', error);
      return null;
    }
  }

  // Clear all authentication data
  clearAuth() {
    this.removeTokens();
  }
}

export default new TokenService(); 